import { z } from 'zod';

const ymdSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, 'Data inválida.');
const hhmmSchema = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/);
const centsSchema = z.number().int().safe().nonnegative();

export const financialProvenanceSchema = z.object({
  source: z.enum(['explicit_statement', 'explicit_correction', 'confirmed_decision']),
  reference: z.string().trim().min(1).max(240).optional(),
}).strict();
export type FinancialProvenanceInput = z.infer<typeof financialProvenanceSchema>;

export const financialFactsSchema = z.object({
  monthlyIncomeCents: centsSchema.nullable().optional(),
  incomeDay: z.number().int().min(1).max(31).nullable().optional(),
  fixedCostsCents: centsSchema.nullable().optional(),
  debtCents: centsSchema.nullable().optional(),
  reserveCents: centsSchema.nullable().optional(),
  riskTolerance: z.enum(['conservative', 'moderate', 'aggressive']).nullable().optional(),
  liquidityNeeds: z.string().trim().min(1).max(500).nullable().optional(),
  monthlySavingsGoalCents: centsSchema.nullable().optional(),
}).strict().refine((facts) => Object.keys(facts).length > 0, 'Informe ao menos um fato explícito.');
export type FinancialFactsInput = z.infer<typeof financialFactsSchema>;

export const updateFinancialProfileBodySchema = z.object({
  confirmation: z.literal('CONFIRMAR APRENDIZADO'),
  facts: financialFactsSchema,
  provenance: financialProvenanceSchema,
}).strict();
export type UpdateFinancialProfileBody = z.infer<typeof updateFinancialProfileBodySchema>;

export const createFinancialGoalBodySchema = z.object({
  requestId: z.string().trim().min(1).max(120),
  name: z.string().trim().min(1).max(120),
  targetAmountCents: centsSchema.positive(),
  currentAmountCents: centsSchema.optional(),
  deadline: ymdSchema.nullable().optional(),
  priority: z.number().int().min(1).max(5).optional(),
  status: z.enum(['active', 'paused', 'completed', 'cancelled']).optional(),
  confirmation: z.literal('CONFIRMAR OBJETIVO'),
  provenance: financialProvenanceSchema,
}).strict();
export type CreateFinancialGoalBody = z.infer<typeof createFinancialGoalBodySchema>;

export const updateFinancialGoalBodySchema = z.object({
  confirmation: z.literal('CONFIRMAR ALTERAÇÃO DE OBJETIVO'),
  name: z.string().trim().min(1).max(120).optional(),
  targetAmountCents: centsSchema.positive().optional(),
  currentAmountCents: centsSchema.optional(),
  deadline: ymdSchema.nullable().optional(),
  priority: z.number().int().min(1).max(5).optional(),
  status: z.enum(['active', 'paused', 'completed', 'cancelled']).optional(),
  provenance: financialProvenanceSchema,
}).strict().refine(
  (body) => Object.keys(body).some((key) => !['confirmation', 'provenance'].includes(key)),
  'Informe ao menos uma alteração.',
);
export type UpdateFinancialGoalBody = z.infer<typeof updateFinancialGoalBodySchema>;

export const deleteFinancialGoalBodySchema = z.object({
  confirmation: z.literal('CONFIRMAR EXCLUSÃO DE OBJETIVO'),
}).strict();
export type DeleteFinancialGoalBody = z.infer<typeof deleteFinancialGoalBodySchema>;

const ntfyTopicSchema = z.string().trim().min(1).max(120).regex(
  /^[A-Za-z0-9_-]+$/,
  'O tópico ntfy deve usar apenas letras, números, _ ou -.',
);
const telegramChatIdSchema = z.string().trim().regex(/^-?[1-9]\d{0,19}$/, 'Chat do Telegram inválido.');

export const updateNotificationPreferencesBodySchema = z.object({
  confirmation: z.literal('CONFIRMAR PREFERÊNCIAS'),
  channel: z.enum(['ntfy', 'telegram', 'both', 'none']),
  quietHoursStart: hhmmSchema.nullable().optional(),
  quietHoursEnd: hhmmSchema.nullable().optional(),
  spendingAlertPercent: z.number().int().min(1).max(100).nullable().optional(),
  weeklyReportEnabled: z.boolean(),
  monthlyReportEnabled: z.boolean(),
  fortnightlyReportEnabled: z.boolean().optional(),
  ntfyTopic: ntfyTopicSchema.nullable().optional(),
  telegramChatId: telegramChatIdSchema.nullable().optional(),
  provenance: financialProvenanceSchema,
}).strict().superRefine((body, ctx) => {
  if ((body.quietHoursStart === null || body.quietHoursStart === undefined)
    !== (body.quietHoursEnd === null || body.quietHoursEnd === undefined)) {
    ctx.addIssue({ code: 'custom', path: ['quietHoursStart'], message: 'Informe início e fim do horário silencioso juntos.' });
  }
  if (body.fortnightlyReportEnabled && (body.channel === 'ntfy' || body.channel === 'both') && !body.ntfyTopic) {
    ctx.addIssue({ code: 'custom', path: ['ntfyTopic'], message: 'Informe o tópico ntfy.' });
  }
  if (body.fortnightlyReportEnabled && (body.channel === 'telegram' || body.channel === 'both') && !body.telegramChatId) {
    ctx.addIssue({ code: 'custom', path: ['telegramChatId'], message: 'Informe o chat do Telegram.' });
  }
  if (body.fortnightlyReportEnabled && body.channel === 'none') {
    ctx.addIssue({ code: 'custom', path: ['channel'], message: 'Escolha ao menos um canal para o resumo financeiro do Sam.' });
  }
});
export type UpdateNotificationPreferencesBody = z.infer<typeof updateNotificationPreferencesBodySchema>;

export const financialFactKeySchema = z.enum([
  'monthlyIncomeCents',
  'incomeDay',
  'fixedCostsCents',
  'debtCents',
  'reserveCents',
  'riskTolerance',
  'liquidityNeeds',
  'monthlySavingsGoalCents',
]);
export type FinancialFactKey = z.infer<typeof financialFactKeySchema>;

export const learnedFactProvenanceSchema = financialProvenanceSchema.extend({
  learnedAt: z.string().datetime(),
});
export type LearnedFactProvenance = z.infer<typeof learnedFactProvenanceSchema>;

const financialProfileFactsResponseSchema = z.object({
  monthlyIncomeCents: centsSchema.optional(),
  incomeDay: z.number().int().min(1).max(31).optional(),
  fixedCostsCents: centsSchema.optional(),
  debtCents: centsSchema.optional(),
  reserveCents: centsSchema.optional(),
  riskTolerance: z.enum(['conservative', 'moderate', 'aggressive']).optional(),
  liquidityNeeds: z.string().min(1).max(500).optional(),
  monthlySavingsGoalCents: centsSchema.optional(),
}).strict();

export const financialGoalSchema = z.object({
  id: z.string(),
  name: z.string(),
  targetAmountCents: centsSchema.positive(),
  currentAmountCents: centsSchema,
  deadline: ymdSchema.nullable(),
  priority: z.number().int().min(1).max(5),
  status: z.enum(['active', 'paused', 'completed', 'cancelled']),
  provenance: learnedFactProvenanceSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type FinancialGoal = z.infer<typeof financialGoalSchema>;

export const notificationPreferencesSchema = z.object({
  channel: z.enum(['ntfy', 'telegram', 'both', 'none']),
  quietHoursStart: hhmmSchema.nullable(),
  quietHoursEnd: hhmmSchema.nullable(),
  spendingAlertPercent: z.number().int().min(1).max(100).nullable(),
  weeklyReportEnabled: z.boolean(),
  monthlyReportEnabled: z.boolean(),
  fortnightlyReportEnabled: z.boolean(),
  ntfyTopic: ntfyTopicSchema.nullable(),
  telegramChatId: telegramChatIdSchema.nullable(),
  provenance: learnedFactProvenanceSchema,
  updatedAt: z.string().datetime(),
});
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

export const financialProfileContextSchema = z.object({
  profile: z.object({
    facts: financialProfileFactsResponseSchema,
    provenance: z.record(financialFactKeySchema, learnedFactProvenanceSchema),
  }),
  goals: z.array(financialGoalSchema),
  notificationPreferences: notificationPreferencesSchema.nullable(),
  investmentReadiness: z.object({
    sufficient: z.boolean(),
    missing: z.array(financialFactKeySchema),
  }),
});
export type FinancialProfileContext = z.infer<typeof financialProfileContextSchema>;
